import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { AdminNavigation } from "../components/navigation/AdminNavigation";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Receipt,
  Loader2,
  Ban,
} from "lucide-react";
import { useAuth } from "../App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Emergent Expense Tracker — artist self-serve. Manual entries + the CRA
// dashboard reads them for the yearly filing. Auto-populated platform fees
// (processor + StardomFans Ltd. 10% cut) show up in a separate section so artists
// see exactly what will be counted for them without needing to enter it.
const Expenses = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    date: now.toISOString().slice(0, 10),
    amount: "",
    category: "",
    memo: "",
  });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/artist/expenses?year=${year}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(data);
      if (!form.category && data.categories?.length) {
        setForm((prev) => ({ ...prev, category: data.categories[0] }));
      }
    } catch (e) {
      if (e.response?.status !== 403) toast.error("Failed to load expenses");
    } finally {
      setLoading(false);
    }
  }, [token, year, form.category]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const add = async () => {
    const amount = parseFloat(form.amount);
    if (!amount || amount === 0) return toast.error("Enter an amount");
    if (!form.category) return toast.error("Pick a category");
    setSaving(true);
    try {
      await axios.post(`${API}/artist/expenses`, {
        date: form.date,
        amount,
        category: form.category,
        memo: form.memo,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Expense added");
      setForm({ ...form, amount: "", memo: "" });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add expense");
    } finally {
      setSaving(false);
    }
  };

  const voidExpense = async (rowId) => {
    const reason = window.prompt("Reason for void:", "");
    if (!reason) return;
    try {
      await axios.post(`${API}/artist/expenses/${rowId}/void`, { reason }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Void offset row appended (original stays for audit)");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Void failed");
    }
  };

  const manualRows = (data?.rows || []).filter((r) => r.source !== "void");
  const voidsByRef = Object.fromEntries((data?.rows || []).filter((r) => r.source === "void").map((r) => [r.voids_row_id, r]));
  const autoTotal = (data?.auto_platform_rows || []).reduce((s, r) => s + r.amount, 0);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-black text-white pb-24" data-testid="expenses-page">
      <header className="sticky top-0 z-30 bg-black/85 backdrop-blur border-b border-[#1A2030]">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-lg border border-[#1A2030] hover:bg-[#1A2030] flex items-center justify-center" data-testid="expenses-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <Link to="/home" className="flex items-center gap-2">
            <img src="/logo.png" alt="StardomFans" className="w-10 h-10 object-contain" style={{ mixBlendMode: "lighten" }} />
            <span className="hidden sm:inline text-white font-bold">StardomFans</span>
          </Link>
          <div className="ml-auto flex items-center gap-3">
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="h-9 px-3 rounded-lg bg-[#050810] border border-[#1A2030] text-white text-sm focus:border-[#EC008C] focus:outline-none"
              data-testid="expenses-year-select"
            >
              {[0, 1, 2, 3].map((n) => {
                const y = now.getFullYear() - n;
                return <option key={y} value={y}>{y}</option>;
              })}
            </select>
            <div className="text-right hidden sm:block">
              <p className="text-[10px] uppercase tracking-widest text-[#8A92A0]">CRA</p>
              <p className="text-white font-bold">Expense Tracker</p>
            </div>
          </div>
        </div>
      </header>
      <AdminNavigation />

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Add expense */}
        <section className="rounded-xl border border-[#1A2030] bg-[#0A0E18] p-5">
          <h2 className="font-bold text-white text-lg mb-3 flex items-center gap-2">
            <Plus className="w-5 h-5 text-[#EC008C]" /> Add expense
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="h-10 px-3 rounded-lg bg-[#050810] border border-[#1A2030] text-white text-sm focus:border-[#EC008C] focus:outline-none"
              data-testid="expense-date-input"
            />
            <input
              type="number"
              step="0.01"
              placeholder="Amount ($)"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              className="h-10 px-3 rounded-lg bg-[#050810] border border-[#1A2030] text-white text-sm focus:border-[#EC008C] focus:outline-none"
              data-testid="expense-amount-input"
            />
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="h-10 px-3 rounded-lg bg-[#050810] border border-[#1A2030] text-white text-sm focus:border-[#EC008C] focus:outline-none"
              data-testid="expense-category-select"
            >
              {(data?.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <button
              onClick={add}
              disabled={saving}
              className="h-10 px-4 rounded-lg bg-[#EC008C] text-black font-bold text-sm inline-flex items-center justify-center gap-2 hover:bg-[#FF3399] disabled:opacity-60"
              data-testid="expense-add-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add
            </button>
          </div>
          <input
            type="text"
            placeholder="Memo (optional — e.g. mic replacement, tour hotel)"
            value={form.memo}
            onChange={(e) => setForm({ ...form, memo: e.target.value })}
            className="mt-3 h-10 w-full px-3 rounded-lg bg-[#050810] border border-[#1A2030] text-white text-sm focus:border-[#EC008C] focus:outline-none"
            data-testid="expense-memo-input"
          />
        </section>

        {/* Manual expenses */}
        <section className="rounded-xl border border-[#1A2030] bg-[#0A0E18] overflow-hidden">
          <div className="px-4 py-3 border-b border-[#1A2030]">
            <p className="text-[10px] uppercase tracking-widest text-[#8A92A0]">Your expenses ({year})</p>
          </div>
          {loading ? (
            <div className="p-8 flex items-center gap-2 text-[#8A92A0]"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : manualRows.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#8A92A0]" data-testid="expenses-empty">No expenses recorded for {year} yet.</div>
          ) : (
            <table className="w-full text-sm" data-testid="expenses-table">
              <thead className="text-[10px] uppercase tracking-widest text-[#8A92A0]">
                <tr className="border-b border-[#1A2030]">
                  <th className="text-left px-4 py-2 font-medium">Date</th>
                  <th className="text-left px-4 py-2 font-medium">Category</th>
                  <th className="text-left px-4 py-2 font-medium">Memo</th>
                  <th className="text-right px-4 py-2 font-medium">Amount</th>
                  <th className="text-right px-4 py-2 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {manualRows.map((r) => {
                  const voided = voidsByRef[r.row_id];
                  return (
                    <tr key={r.row_id} className={`border-b border-[#1A2030] ${voided ? "opacity-40" : ""}`} data-testid={`expense-row-${r.row_id}`}>
                      <td className="px-4 py-2 text-white text-xs">{r.date}</td>
                      <td className="px-4 py-2 text-white text-xs">{r.category}</td>
                      <td className="px-4 py-2 text-[#8A92A0] text-xs truncate max-w-xs">{r.memo}</td>
                      <td className="px-4 py-2 text-right text-white text-xs">${r.amount.toFixed(2)}</td>
                      <td className="px-4 py-2 text-right">
                        {voided ? (
                          <span className="text-[10px] text-[#4A5060]">Voided</span>
                        ) : (
                          <button
                            onClick={() => voidExpense(r.row_id)}
                            className="text-[10px] text-[#EC008C] hover:underline inline-flex items-center gap-1"
                            data-testid={`expense-void-${r.row_id}`}
                          >
                            <Ban className="w-3 h-3" /> Void
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </section>

        {/* Auto-linked platform fees */}
        <section className="rounded-xl border border-[#F0C464]/30 bg-[#F0C464]/5 overflow-hidden">
          <div className="px-4 py-3 border-b border-[#F0C464]/20 flex items-center gap-2">
            <Receipt className="w-4 h-4 text-[#F0C464]" />
            <p className="text-[10px] uppercase tracking-widest text-[#F0C464]">Auto-linked platform costs · ${autoTotal.toFixed(2)} total</p>
          </div>
          <p className="px-4 py-3 text-xs text-[#8A92A0]">
            These are computed on the fly from your paid transactions — Stripe processing + payout fees + StardomFans Ltd. 12% commission (10% for Founders Circle). They&apos;ll appear in your CRA export automatically. No entry required.
          </p>
          {data?.auto_platform_rows && data.auto_platform_rows.length > 0 && (
            <table className="w-full text-sm" data-testid="expenses-auto-table">
              <thead className="text-[10px] uppercase tracking-widest text-[#8A92A0]">
                <tr className="border-t border-[#F0C464]/20">
                  <th className="text-left px-4 py-2 font-medium">Date</th>
                  <th className="text-left px-4 py-2 font-medium">Category</th>
                  <th className="text-left px-4 py-2 font-medium">Memo</th>
                  <th className="text-right px-4 py-2 font-medium">Amount</th>
                </tr>
              </thead>
              <tbody>
                {data.auto_platform_rows.slice(0, 20).map((r, i) => (
                  <tr key={i} className="border-t border-[#F0C464]/10">
                    <td className="px-4 py-1.5 text-white text-xs">{r.date}</td>
                    <td className="px-4 py-1.5 text-white text-xs">{r.category}</td>
                    <td className="px-4 py-1.5 text-[#8A92A0] text-xs truncate max-w-xs">{r.memo}</td>
                    <td className="px-4 py-1.5 text-right text-white text-xs">${r.amount.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </main>
    </div>
  );
};

export default Expenses;
